import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useEffect, useMemo } from 'react'
import fetchMerchandiseCategories from 'actions/merchandise/fetchMerchandiseCategories'
import { arrayToMap, EmptyArray, isNonNullable } from 'lib/array/arrayUtils'
import useStableObjectReference from 'hooks/useStableObjectReference'

type ReturnType = [
  Array<App.MerchandiseCategory>,
  boolean,
  any,
]

interface Params {
  categoryIds?: Array<string>;
  categorySlugs?: Array<string>;
}

interface Options {
  disabled?: boolean;
}

function useMerchandiseCategories(params: Params = {}, options: Options = {}): ReturnType {
  const dispatch = useAppDispatch()
  const categories = useAppSelector(state => state.merchandise.merchandiseCategories)

  const stableIds = useStableObjectReference(params.categoryIds)
  const stableSlugs = useStableObjectReference(params.categorySlugs)

  const fetching = useAppSelector(state => {
    const fetchingSlugs = params.categorySlugs?.some(slug => state.merchandise.fetchingMerchandiseCategories[slug])
    const fetchingIds = params.categoryIds?.some(id => state.merchandise.fetchingMerchandiseCategories[id])
    return !!fetchingSlugs || !!fetchingIds
  })
  const error = useAppSelector(state => {
    const errorSlugs = params.categorySlugs?.find(slug => state.merchandise.merchandiseCategoryErrors[slug])
    const errorIds = params.categoryIds?.find(id => state.merchandise.merchandiseCategoryErrors[id])
    return errorSlugs ?? errorIds
  })

  useEffect(() => {
    if (!options.disabled) {
      dispatch(fetchMerchandiseCategories({
        categorySlugs: stableSlugs,
        categoryIds: stableIds,
      }))
    }
  }, [dispatch, options.disabled, stableIds, stableSlugs])

  const categoriesArray = useMemo(() => {
    const allCategories = Object.values(categories)
    if (params.categoryIds) {
      return params.categoryIds.map(id => categories[id]).filter(isNonNullable)
    }

    if (params.categorySlugs) {
      const categoriesBySlug = arrayToMap(allCategories, c => c.slug.toLowerCase())
      return params.categorySlugs.map(slug => categoriesBySlug.get(slug.toLowerCase())).filter(isNonNullable)
    }

    return allCategories
  }, [categories, params.categoryIds, params.categorySlugs])

  return [
    options.disabled ? EmptyArray : categoriesArray,
    fetching && !options.disabled,
    error,
  ]
}

export default useMerchandiseCategories
